import { parseIsoDate } from './dates.js';
import type { TaskNotifyChannel, TaskNotifyOptions } from './schemas/task-notify.js';

/**
 * Lot AZ « Le prévenir » : textes partagés par l'écran (message prérempli, canal proposé) et le serveur (lien,
 * objet du mail, notification interne).
 */

/** « 22/10 » */
export function slashDayMonth(value: string): string {
  const { month, day } = parseIsoDate(value);
  return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}`;
}

/** Prénom affiché (« Inès » pour « Inès Durand »). */
export const taskNotifyFirstName = (name: string) => name.trim().split(/\s+/)[0] ?? name;

/**
 * Message prérempli (maquette) : « Je t'ai ajouté une tâche pour le 22/10 : <titre>. » à la création, « Je t'ai
 * passé une carte pour le 22/10 : <titre>. » à la réattribution. Le lien vers la carte est ajouté à l'envoi.
 */
export function taskNotifyMessage(input: {
  kind: 'created' | 'reassigned';
  title: string;
  dueDate: string;
}): string {
  const title = input.title.trim().replace(/[.\s]+$/, '');
  const what = input.kind === 'created' ? 'ajouté une tâche' : 'passé une carte';
  return `Je t'ai ${what} pour le ${slashDayMonth(input.dueDate)} : ${title}.`;
}

/** Chemin de la carte dans l'appli (`/a-faire?action=<id>`), comme les notifications « Nouvelle tâche ». */
export const taskPath = (actionId: string) => `/a-faire?action=${actionId}`;

/** Adresse absolue de la carte (adresse publique de l'appli, sans barre finale). */
export function taskUrl(appUrl: string, actionId: string): string {
  return `${appUrl.replace(/\/+$/, '')}${taskPath(actionId)}`;
}

/** Texte envoyé : le message tel que relu, puis le lien vers la carte. */
export function taskNotifyText(message: string, url: string): string {
  return `${message.trim()}\n\nVoir dans le Cockpit : ${url}`;
}

/** Objet du mail : « Nouvelle tâche : <titre> » (titre coupé à 120 caractères). */
export function taskNotifyMailSubject(title: string): string {
  const clean = title.trim().replace(/\s+/g, ' ');
  return `Nouvelle tâche : ${clean.length > 120 ? `${clean.slice(0, 119)}…` : clean}`;
}

/**
 * Notification interne (cloche + téléphone selon `push.newTask`) quand quelqu'un d'autre ajoute une tâche ou passe
 * une carte : « Nouvelle tâche pour toi : <titre> » · « Par Maxime » (« Passée par Maxime » pour une carte
 * réattribuée).
 */
export function newTaskNotification(input: {
  kind: 'created' | 'reassigned';
  title: string;
  actorName: string;
  actionId: string;
}): { title: string; body: string; url: string } {
  const by = taskNotifyFirstName(input.actorName);
  return {
    title: `Nouvelle tâche pour toi : ${input.title}`,
    body: input.kind === 'created' ? `Par ${by}` : `Passée par ${by}`,
    url: taskPath(input.actionId),
  };
}

/**
 * Canal proposé : le dernier choisi, sauf s'il n'est pas utilisable (l'autre s'il l'est). Rend null quand aucun
 * canal n'est utilisable.
 */
export function proposedTaskNotifyChannel(
  options: Pick<TaskNotifyOptions, 'chat' | 'email' | 'lastChannel'>,
): TaskNotifyChannel | null {
  const other: TaskNotifyChannel = options.lastChannel === 'chat' ? 'email' : 'chat';
  if (options[options.lastChannel].available) return options.lastChannel;
  return options[other].available ? other : null;
}
